/* eslint no-restricted-syntax: 0 */
// css modules
import styles from './App.module.scss';

// node_modules
import cx from 'classnames';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// root imports
import { GridContainerThemeProvider } from 'components/GridContainerThemeProvider/GridContainerThemeProvider';
import { useActiveInput } from 'hooks/useActiveInput';
import { useCaptureScrollbar } from 'hooks/useCaptureScrollbar';
import { setExistingMerchantCookieWithSubDomains } from 'utils/ExistingMerchant';
import { useHandleEvidonCookieConsent } from 'hooks/useHandleEvidonCookieConsent';

// relative imports
import { useLocation, Redirect } from 'react-router-dom';
import { CookieBanner } from 'components/CookieBanner/CookieBanner';
import { useScrollPositionContext } from 'components/ScrollPositionProvider/ScrollPositionContext';
import { SkipNav } from 'components/SkipNavigation/SkipNav';
import { InternationalRedirects } from './Routes/InternationalRedirects/InternationalRedirects';
import { Routes } from './Routes/Routes';
import { useCookieConfig } from 'hooks/useCookieConfig';
import { useScrollOnRender } from 'hooks/useScrollOnRender';
import { SiteHeader } from 'components/SiteHeader/SiteHeader';
import { useContentEntriesContext } from 'components/ContentEntriesProvider/ContentEntriesContext';
import { useV2Route } from 'hooks/useV2Route';
import { SiteFooter } from '../SiteFooter/SiteFooter';
import { useFiMerchant } from 'hooks/useFiMerchant';
import FiIntegrationUtils from 'utils/FiIntegrationUtils';

export const App: React.FC = () => {
  const { i18n } = useTranslation();
  const userActiveInput = useActiveInput();
  const isV2 = useV2Route();
  const { hasPageLoaded } = useScrollPositionContext();
  const { activePage } = useContentEntriesContext();
  const hasHeaderTop = !activePage?.transparentNavBar;
  const location = useLocation();
  const { shouldHideHeaderFooterForFi, shouldRedirectFiUser, subscriberRefId } = useFiMerchant();

  useCookieConfig();
  useCaptureScrollbar();
  useScrollOnRender();
  useHandleEvidonCookieConsent();

  const subscriberRefIdParam = FiIntegrationUtils.getSubscriberRefIdQueryParam();

  const isUSLocale = i18n.language === 'en-US';
  const isByo = location.pathname.includes('/build-your-own');
  const isWelcomePage = location.pathname === '/welcome';
  const shouldShowFooterBasedOnPathName = !isByo && !isWelcomePage;
  const showHeader = !subscriberRefIdParam && !shouldHideHeaderFooterForFi;
  const showFooter = hasPageLoaded && shouldShowFooterBasedOnPathName && !shouldHideHeaderFooterForFi;

  useEffect(() => {
    setExistingMerchantCookieWithSubDomains();
  }, []);

  useEffect(() => {
    document.body.dataset.activeInput = userActiveInput;
  }, [userActiveInput]);

  if (shouldRedirectFiUser) {
    return <Redirect to="/purchase/banking-offer" />;
  }

  return (
    <GridContainerThemeProvider>
      <div className={styles.app}>
        <InternationalRedirects>
          <SkipNav id="skip-nav" targetDomId="main" />
          {showHeader && <SiteHeader />}
          <main
            id="main"
            className={cx(styles.main, {
              [styles.mainNoUsLang]: !isUSLocale,
              [styles.hasHeaderTop]: hasHeaderTop && showHeader,
              [styles.isV2]: isV2 && showHeader,
              [styles.mainIsBankingOfferModule]: subscriberRefId,
            })}
          >
            {!isUSLocale && <CookieBanner />}
            <Routes />
          </main>
          {showFooter && <SiteFooter />}
        </InternationalRedirects>
      </div>
    </GridContainerThemeProvider>
  );
};
